/**
 * Master Data Edit Utilities Module
 */

import type { ZodTypeAny } from "zod";

type MasterDataEditUtilsScalar = string | number | boolean | null | undefined | File;

type MasterDataEditUtilsValue = MasterDataEditUtilsScalar | MutablePayload | MasterDataEditUtilsValue[];

type MutablePayload = { [key: string]: MasterDataEditUtilsValue };

interface ResourceNormalizationRules {
    intFields?: string[];
    decimalFields?: string[];
    dateTimeFields?: string[];
    csvArrayFields?: string[];
    csvIntArrayFields?: string[];
    jsonArrayStringFields?: string[];
    nullableFields?: string[];
    omitAlways?: string[];
    omitIfEmpty?: string[];
}

const RESOURCE_NORMALIZATION_RULES: Record<string, ResourceNormalizationRules> = {
    brands: {
        intFields: ["priority"],
        dateTimeFields: ["activate_time"],
    },
    products: {
        intFields: ["category_id", "brand_id", "manual_score_offset"],
        decimalFields: ["price"],
        omitAlways: ["product_image"],
        omitIfEmpty: ["group_id"],
    },
    users: {
        jsonArrayStringFields: [
            "skin_concerns",
            "hair_scalp_concerns",
            "beauty_wellness_interests",
            "lifestyle_environment",
            "frequent_category_ids",
            "frequent_brand_ids",
        ],
        omitAlways: ["search_terms"],
        omitIfEmpty: [
            "skin_concerns",
            "hair_scalp_concerns",
            "beauty_wellness_interests",
            "lifestyle_environment",
            "frequent_category_ids",
            "frequent_brand_ids",
            "full_name",
        ],
    },
};

function getZodTypeName(schema: unknown): string | null {
    if (!schema || typeof schema !== "object") return null;

    const def = Reflect.get(schema, "_def");
    if (!def || typeof def !== "object") return null;

    const typeName = Reflect.get(def, "typeName");
    return typeof typeName === "string" ? typeName : null;
}

function getZodInnerType(schema: unknown): unknown {
    if (!schema || typeof schema !== "object") return null;

    const def = Reflect.get(schema, "_def");
    if (!def || typeof def !== "object") return null;

    return Reflect.get(def, "innerType") ?? Reflect.get(def, "schema") ?? null;
}

function schemaAllowsNull(schema: unknown): boolean {
    const typeName = getZodTypeName(schema);
    if (!typeName) return false;

    if (typeName === "ZodNullable" || typeName === "ZodNull") {
        return true;
    }

    if (
        typeName === "ZodOptional" ||
        typeName === "ZodDefault" ||
        typeName === "ZodEffects" ||
        typeName === "ZodBranded" ||
        typeName === "ZodReadonly" ||
        typeName === "ZodCatch" ||
        typeName === "ZodPipeline"
    ) {
        return schemaAllowsNull(getZodInnerType(schema));
    }

    if (typeName === "ZodUnion") {
        const def = Reflect.get(schema as object, "_def");
        const options = def && typeof def === "object" ? Reflect.get(def, "options") : null;
        if (!Array.isArray(options)) return false;

        return options.some((option) => schemaAllowsNull(option));
    }

    return false;
}

function getSchemaShape(schema: ZodTypeAny | null | undefined): Record<string, unknown> {
    if (!schema || typeof schema !== "object") return {};

    let current: unknown = schema;

    while (current && typeof current === "object") {
        const typeName = getZodTypeName(current);
        if (typeName === "ZodObject") {
            const def = Reflect.get(current, "_def");
            const shape = def && typeof def === "object" ? Reflect.get(def, "shape") : null;
            if (typeof shape === "function") {
                const resolved = shape();
                return resolved && typeof resolved === "object" ? (resolved as Record<string, unknown>) : {};
            }
            return shape && typeof shape === "object" ? (shape as Record<string, unknown>) : {};
        }

        if (
            typeName === "ZodOptional" ||
            typeName === "ZodNullable" ||
            typeName === "ZodDefault" ||
            typeName === "ZodEffects" ||
            typeName === "ZodBranded" ||
            typeName === "ZodReadonly" ||
            typeName === "ZodCatch" ||
            typeName === "ZodPipeline"
        ) {
            current = getZodInnerType(current);
            continue;
        }

        break;
    }

    return {};
}

function getNullableFieldsFromSchema(schema: ZodTypeAny | null | undefined, fallback: string[]): string[] {
    const shape = getSchemaShape(schema);
    const fields = Object.entries(shape)
        .filter(([, fieldSchema]) => schemaAllowsNull(fieldSchema))
        .map(([field]) => field);

    return fields.length > 0 ? fields : fallback;
}

function buildUserFullName(payload: MutablePayload): string {
    const first = String(payload.first_name ?? "").trim();
    const middle = String(payload.middle_name ?? "").trim();
    const last = String(payload.last_name ?? "").trim();
    return [first, middle, last].filter(Boolean).join(" ").trim();
}

function toDatetimeLocalValue(value: MasterDataEditUtilsValue): string {
    if (typeof value !== "string") return "";
    const s = value.trim();
    if (!s) return "";

    if (/Z$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
        const d = new Date(s);
        if (!Number.isNaN(d.getTime())) {
            const pad = (n: number): string => String(n).padStart(2, "0");
            const yyyy = d.getFullYear();
            const mm = pad(d.getMonth() + 1);
            const dd = pad(d.getDate());
            const hh = pad(d.getHours());
            const mi = pad(d.getMinutes());
            return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
        }
    }

    const replaced = s.replace(" ", "T");
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(replaced)) {
        return replaced.slice(0, 16);
    }
    return "";
}

function toApiDateTimeValue(value: MasterDataEditUtilsValue): string | null {
    if (typeof value !== "string") return null;

    const trimmed = value.trim();
    if (!trimmed) return null;

    const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed) ? `${trimmed}:00` : trimmed.replace(" ", "T");

    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
        return trimmed;
    }

    return parsed.toISOString();
}

function normalizePayload(resource: string, payload: MutablePayload, schema?: ZodTypeAny | null): MutablePayload {
    const rules = RESOURCE_NORMALIZATION_RULES[resource] ?? {};

    const nullableFields = getNullableFieldsFromSchema(schema, rules.nullableFields || []);
    for (const key of nullableFields) {
        if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
        if (payload[key] === "") {
            payload[key] = null;
        }
    }

    const intFields = rules.intFields || [];
    for (const key of intFields) {
        if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
        if (payload[key] === "") {
            payload[key] = null;
            continue;
        }
        const n = Number(payload[key]);
        payload[key] = Number.isFinite(n) ? n : payload[key];
    }

    const decimalFields = rules.decimalFields || [];
    for (const key of decimalFields) {
        if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
        if (payload[key] === "") {
            payload[key] = null;
            continue;
        }
        const normalized = String(payload[key] ?? "")
            .trim()
            .replace(/,/g, ".");
        const n = Number(normalized);
        payload[key] = Number.isFinite(n) ? n : payload[key];
    }

    const dateTimeFields = rules.dateTimeFields || [];
    for (const key of dateTimeFields) {
        if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
        if (payload[key] === "") {
            payload[key] = null;
            continue;
        }

        const normalized = toApiDateTimeValue(payload[key]);
        payload[key] = normalized ?? null;
    }

    const csvArrayFields = rules.csvArrayFields || [];
    for (const key of csvArrayFields) {
        if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
        if (payload[key] === null) continue;
        if (payload[key] === "") {
            payload[key] = [];
            continue;
        }
        const s = String(payload[key] ?? "");
        payload[key] = s
            .split(",")
            .map((entry) => entry.trim())
            .filter(Boolean);
    }

    const csvIntArrayFields = rules.csvIntArrayFields || [];
    for (const key of csvIntArrayFields) {
        if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
        if (payload[key] === null) continue;
        if (payload[key] === "") {
            payload[key] = [];
            continue;
        }
        const s = String(payload[key] ?? "");
        payload[key] = s
            .split(",")
            .map((entry) => Number(String(entry).trim()))
            .filter((entry) => Number.isFinite(entry) && entry > 0)
            .map((entry) => Math.trunc(entry));
    }

    const jsonArrayStringFields = rules.jsonArrayStringFields || [];
    for (const key of jsonArrayStringFields) {
        if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;

        const value = payload[key];
        if (!Array.isArray(value)) continue;

        const normalized = value.map((entry) => String(entry).trim()).filter(Boolean);

        if (normalized.length === 0) {
            delete payload[key];
            continue;
        }

        payload[key] = JSON.stringify(normalized);
    }

    const omitAlways = rules.omitAlways || [];
    for (const key of omitAlways) {
        delete payload[key];
    }

    const omitIfEmpty = rules.omitIfEmpty || [];
    for (const key of omitIfEmpty) {
        if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
        if (payload[key] === "" || payload[key] === null || payload[key] === undefined) {
            delete payload[key];
        }
    }

    return payload;
}

export { buildUserFullName, toDatetimeLocalValue, normalizePayload };
